<template>
    <section class="basket-summary-section mb-4">
        <div class="full-width pt-4 pb-4">
            <div class="content">
                <div class="row">
                    <div class="col-lg-4 hidden-until-lg">
                        <div class="basket-delivery">
                            <h3>{{ $t('basket.delivery') }}</h3>

                            <div>
                                <span v-html="deliveryText" />
                            </div>

                            <div class="mt-3">
                                <a href="/" class="text-dark align-self-center mt-2">
                                    {{ $t('basket.continue_shopping') }}
                                </a>
                            </div>
                        </div>
                    </div>
                    <div class="col-lg-4 offset-lg-4">
                        <div class="basket-summary">

                            <div class="basket-summary-top">
                                <div class="basket-summary-item d-flex justify-content-between">
                                    <div>{{ $t('basket.subtotal') }}</div>
                                    <div class="price">
                                        <span
                                            v-if="isBusinessUser"
                                        >
                                            {{ summary.subtotal.exc | currency }}
                                        </span>
                                        <span
                                            v-else
                                        >
                                            {{ summary.subtotal.inc | currency }}
                                        </span>
                                    </div>
                                </div>

                                <div v-if="isBusinessUser">
                                    <div
                                        v-for="(vat, index) in summary.vats"
                                        class="basket-summary-item d-flex justify-content-between"
                                    >
                                        <div>{{ $t('basket.vat') }} {{ index }} %</div>
                                        <div class="price">{{ vat | currency }}</div>
                                    </div>
                                </div>
                            </div>

                            <div class="basket-summary-bottom">
                                <div class="basket-summary-item d-flex justify-content-between">
                                    <div>{{ $t('basket.discount_code') }}</div>
                                    <div class="w-100">

                                        <form
                                            @submit.prevent="discountCodeSubmit"
                                            action=""
                                            method="post"
                                            class="basket-summary-discount d-flex flex-column justify-content-between"
                                        >
                                            <input type="hidden" name="_token" :value="csrfToken">
                                            <button
                                                @click="discountOpen = !discountOpen"
                                                :class="[{ 'sr-only': discountOpen }, 'text-dark text-right text-nowrap']"
                                                type="button"
                                            >
                                                {{ $t('basket.add_discount_code') }}
                                            </button>

                                            <div :class="[{ 'sr-only': !discountOpen }, 'form-group d-flex flex-column mb-0']">
                                                <input
                                                    v-model="discountCode"
                                                    :placeholder="$t('basket.add_discount_code_placeholder')"
                                                    type="text"
                                                    id="query"
                                                    name="q"
                                                    class="form-control"
                                                    required
                                                >
                                                <label for="query">
                                                    {{ $t('basket.add_discount_code_placeholder') }}
                                                </label>

                                                <small
                                                    v-show="discountCodeMessage !== ''"
                                                    class="text-danger mt-1"
                                                >
                                                    {{ $t(discountCodeMessage) }}
                                                </small>

                                                <button class="btn btn-sm btn-primary mt-1">
                                                    {{ $t('basket.activate') }}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                                <div v-if="summary.discount.voucher > 0" class="basket-summary-item d-flex justify-content-between">
                                    <div>{{ $t('basket.discount_code') }} ({{summary.voucher_code }})
                                        <a @click="removeVoucher">{{ $t('basket.remove') }}</a></div>
                                    <div class="price"> - {{ summary.discount.voucher | currency }}</div>
                                </div>

                                <div v-if="summary.discount.customer > 0" class="basket-summary-item d-flex justify-content-between">
                                    <div>{{ $t('basket.customer_discount')}}</div>
                                    <div class="price"> - {{ summary.discount.customer | currency }}</div>
                                </div>

                                <div class="basket-summary-item d-flex justify-content-between">
                                    <div>
                                        {{ $t('basket.shipping_costs') }}

                                        <p
                                            v-if="summary.shipping_threshold_left !== 0"
                                            class="basket-summary-free-shipping mt-1"
                                        >
                                            {{ $t('basket.free_shipping', {
                                                'threshold': formatCurrency(summary.shipping_threshold),
                                                'threshold_left': formatCurrency(summary.shipping_threshold_left),
                                            }) }}
                                        </p>
                                    </div>
                                    <div class="price">
                                        <span
                                            v-if="isBusinessUser"
                                        >
                                            {{ summary.shipping_costs.exc | currency }}
                                        </span>
                                        <span
                                            v-else
                                        >
                                            {{ summary.shipping_costs.inc | currency }}
                                        </span>
                                    </div>
                                </div>

                                <div v-if="isBusinessUser && summary.shipping_threshold_left !== 0">
                                    <div
                                        class="basket-summary-item d-flex justify-content-between"
                                    >
                                        <div>{{ $t('basket.vat') }} {{ summary.shipping_vat }} %</div>
                                        <div class="price">{{ summary.shipping_vat_amount | currency }}</div>
                                    </div>
                                </div>

                                <form
                                    v-if="summary.gift_wrap_costs !== 0 && !isBusinessUser"
                                    action=""
                                    method="post"
                                    class="basket-summary-gift mt-2"
                                >
                                    <input type="hidden" name="_token" :value="csrfToken">

                                    <div class="form-group form-check mb-0">
                                        <input
                                            @change="setGiftWrap"
                                            :checked="summary.gift_wrap"
                                            type="checkbox"
                                            id="basketGiftWrap"
                                            class="form-check-input"
                                        >
                                        <label for="basketGiftWrap" class="form-check-label text-nowrap">
                                            {{ $t('basket.gift_wrap') }}? ({{ summary.gift_wrap_costs | currency }})
                                        </label>
                                    </div>
                                </form>

                                <div class="basket-summary-item basket-summary-total d-flex justify-content-between mt-1">
                                    <div>{{ $t('basket.total') }}</div>
                                    <div class="price text-nowrap">{{ summary.total | currency }}</div>
                                </div>

                                <div class="d-flex flex-column">
                                    <a :href="route('cart.auth')" class="btn btn-primary">
                                        {{ $t('basket.order') }}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
    import axios from 'axios';
    import { mapActions, mapState } from 'vuex';

    export default {
        name: 'BasketSummary',
        data() {
            return {
                discountCode: '',
                discountCodeMessage: '',
                discountOpen: false,
            };
        },
        computed: {
            ...mapState('basket', {
                deliveryText: 'deliveryText',
                summary: 'summary',
            }),
        },
        methods: {
            ...mapActions('basket', [
                'fetchBasket',
            ]),

            async discountCodeSubmit() {
                this.discountCodeMessage = '';

                const { data } = await axios.post(this.route('cart.discount.update'), {
                    discount_code: this.discountCode,
                });

                if (data.success) {
                    this.discountCode = '';
                    this.discountOpen = false;
                    this.fetchBasket();
                } else this.discountCodeMessage = data.message;
            },

            async setGiftWrap() {
                await axios.post(this.route('cart.gift-wrap.update'));
                this.fetchBasket();
            },

            async removeVoucher() {
                const { data } = await axios.post(this.route('cart.discount.update'), {
                    discount_code: '',
                    remove: true,
                });

                if (data.success) {
                    this.discountCode = '';
                    this.discountOpen = false;
                    this.fetchBasket();
                }
            },
        },
    };
</script>
